import { ALL_VENDOR_OPTION, ECSegment, ECSegmentFormData, ECSegmentListDTO, ECSegmentRulesDetail, ECSegmentStatus, ECSegmentType } from 'core/ecSegment/ECSegment';
import { AxiosInstance } from 'axios';
import client from './RestClient';
import { omit } from 'lodash';
import { SelectOptions } from 'components/commonType';

export interface ECSegmentWebService {
  getECSegments (
    advertiserId: number | undefined,
    status?: ECSegmentStatus[]
  ): Promise<ECSegmentListDTO[]>;
  getECSegment (id: string): Promise<ECSegment>;
  createECSegment (segmentData: Partial<ECSegmentFormData>): Promise<void>;
  updateECSegment (id: string, segmentData: Partial<ECSegmentFormData>): Promise<void>;
  deleteECSegments (ids: string[]): Promise<void>;
  intersectECSegments (advertiserId: number, name: string, id1: string, id2: string): Promise<void>;
  getInUsedVendorNumbers (agencyId: number | string, vendorNumbers: string[]): Promise<SelectOptions[]>;
  getECSegmentRules (id: string): Promise<ECSegmentRulesDetail>;
}

const wrapECSegmentList = (json: any): ECSegmentListDTO[] => {
  return json.map((item: any) => ({
    ...item,
    id: item.segmentId
  }));
};
export class RestfulECSegmentWebService implements ECSegmentWebService {
  restClient: AxiosInstance;

  constructor (restClient: AxiosInstance = client) {
    this.restClient = restClient;
  }

  async getECSegments (
    advertiserId: number | undefined,
    status: ECSegmentStatus[] = [
      ECSegmentStatus.ACTIVE,
      ECSegmentStatus.UNBOUND,
      ECSegmentStatus.PENDING,
      ECSegmentStatus.ARCHIVED,
      ECSegmentStatus.UPDATING,
      ECSegmentStatus.OVERLY_RESTRICTIVE
    ]
  ): Promise<ECSegmentListDTO[]> {
    let url = `/v2/ec-segments?status=${status.join(',')}`;
    if (advertiserId) {
      url += `&advertiser_id=${advertiserId}`;
    }
    const response = await this.restClient.get(url);
    return wrapECSegmentList(response.data.records);
  }

  async getECSegment (id: string): Promise<ECSegment> {
    const response = await this.restClient.get(`/v2/ec-segments/${id}`);
    return omit({
      ...response.data,
      id: response.data.segmentId,
      vendorNumber:
        response.data.type === ECSegmentType.PRODUCT_CATEGORY
          ? response.data.vendorNumber || ALL_VENDOR_OPTION.value.toString()
          : undefined
    }) as ECSegment;
  }

  async createECSegment (segmentData: Partial<ECSegmentFormData>): Promise<void> {
    return this.restClient.post('/v2/ec-segments', segmentData);
  }

  async updateECSegment (id: string, segmentData: Partial<ECSegmentFormData>): Promise<void> {
    return this.restClient.put('/v2/ec-segments', {
      segmentId: id,
      ...segmentData
    });
  }

  async deleteECSegments (ids: string[]): Promise<void> {
    return this.restClient.delete('/v2/ec-segments', { data: ids });
  }

  async intersectECSegments (advertiserId: number, name: string, id1: string, id2: string): Promise<void> {
    return this.restClient.post('/v2/ec-segments/intersect', {
      advertiserId,
      name,
      segmentIds: [id1, id2]
    });
  }

  async getInUsedVendorNumbers (agencyId: number | string, vendorNumbers: string[]): Promise<SelectOptions[]> {
    let url = `/v2/ec-segments/inUseVendorNumbers?agencyId=${agencyId}&vendorNumbers=${vendorNumbers.join(',')}`;
    const response = await this.restClient.get(url);
    return response.data.records.map((item: any) => ({
      label: item.label,
      value: item.value
    }));
  }

  async getECSegmentRules (id: string): Promise<ECSegmentRulesDetail> {
    const response = await this.restClient.get(`/v2/ec-segments/${id}/rules`);
    return response.data;
  }
}
